import { ComponentRepresentation } from '@ardoq/basic-components';
import { RepresentationData } from '@ardoq/data-model';
import { useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { CUSTOM_SVG_ICON_SELECTOR, IconSize } from '@ardoq/icons';
import { returnZero } from '@ardoq/common-helpers';
import { TextOverflow, WithPopover } from '@ardoq/popovers';
import { getComponentLabelParts } from './truncateText/getComponentLabelParts';
import { useResizeObserver } from '@ardoq/hooks';
import { truncateComponentLabel } from './truncateText/truncateComponentLabel';
import { fontFamily } from '@ardoq/design-tokens';
import { canvasLabelMeasurer } from '@ardoq/graph-labeling';

const ICON_MARGIN = 8;
const ICON_SIZE = 18;

// text2 typography: fontSize.regular (14px) / lineHeight.regular (20px)
const COMPONENT_LABEL_FONT = `400 14px/20px ${fontFamily.regular}`;
const measure = canvasLabelMeasurer(COMPONENT_LABEL_FONT);

const IconContainer = styled.div`
  display: flex;
  justify-content: center;
  margin: 0 ${ICON_MARGIN}px 0 0;
  height: ${ICON_SIZE}px;
  min-width: ${ICON_SIZE}px;
  font-size: ${ICON_SIZE}px;
  text-align: center;
  max-width: min-content;

  > ${CUSTOM_SVG_ICON_SELECTOR} {
    width: -webkit-fill-available;
  }
`;
export type TruncatingComponentLabelProperties = {
  representationData?: RepresentationData | null;
  id?: string;
  labelClassName?: string;
  iconClassName?: string;
  iconColor?: string;
};

export const TruncatingComponentLabel = ({
  representationData,
  id,
  labelClassName,
  iconClassName,
  iconColor,
}: TruncatingComponentLabelProperties) => {
  const container = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  useResizeObserver(container, ({ width }) => {
    setWidth(width);
  });

  const { label, fieldLabel, fieldValue } = getComponentLabelParts(id || '');

  const truncatedFieldLabelAndValue = useMemo(
    () =>
      truncateComponentLabel({
        fieldValue: fieldValue,
        fieldLabel: fieldLabel,
        label: '',
        width,
        measure,
      }).trim(),
    [fieldLabel, fieldValue, width]
  );

  const fullFieldLabelAndValue = truncateComponentLabel({
    fieldLabel,
    fieldValue,
    label: '',
    width: Infinity,
    measure: returnZero,
  }).trim();
  const isFieldLabelAndValueTruncated = !!(
    truncatedFieldLabelAndValue &&
    fullFieldLabelAndValue &&
    truncatedFieldLabelAndValue !== fullFieldLabelAndValue
  );
  const fullLabelFormatted = `${label}\n${fullFieldLabelAndValue}`;

  const popoverContent = isFieldLabelAndValueTruncated
    ? fullLabelFormatted
    : null;
  const textOverflow = (
    <TextOverflow
      lineClamp={fieldValue ? 2 : 3}
      textContent={fullLabelFormatted}
      renderIn={isFieldLabelAndValueTruncated ? null : 'popover'}
    >
      {label || ''}
    </TextOverflow>
  );

  const labelStyle = {
    maxWidth: representationData
      ? `calc(100% - ${ICON_SIZE + ICON_MARGIN * 2}px)`
      : '100%',
  };

  return (
    <>
      {representationData && (
        <IconContainer className={iconClassName}>
          <ComponentRepresentation
            isImage={representationData.isImage}
            value={representationData.value}
            icon={representationData.icon}
            style={{ color: iconColor, stroke: iconColor }}
            iconSize={ICON_SIZE as IconSize}
          />
        </IconContainer>
      )}
      <div className={labelClassName} ref={container} style={labelStyle}>
        <WithPopover content={popoverContent}>
          {textOverflow}
          <span>{truncatedFieldLabelAndValue}</span>
        </WithPopover>
      </div>
    </>
  );
};
